import type { Trip } from "../types";
import { dayCity } from "./trip-route";

/**
 * Which day a place goes on when the guest taps "Add to trip" (owner,
 * 2026-11-04, option A): the first day in the place's own city that still has
 * room, else the emptiest day in that city, else the emptiest day of the
 * trip. Before this every place went on the last day, even in another city.
 *
 * Pure: web and app both import it.
 */

/** Stops a day holds before it counts as full: the planner's three slots. */
export const DAY_ROOM = 3;

/**
 * Places that sit just outside a city but are planned from it: a day in
 * Marrakech is where an Agafay camp or quad goes.
 */
const PLANNED_FROM: Readonly<Record<string, string>> = {
  agafay: "marrakech",
  "ourika-valley": "marrakech",
  ourika: "marrakech",
  taghazout: "agadir",
  imsouane: "agadir",
};

/** Whether a place in `placeCity` belongs on a day in `dayCitySlug`. */
export function placeFitsCity(placeCity: string | null | undefined, dayCitySlug: string): boolean {
  if (!placeCity) return true;
  return placeCity === dayCitySlug || PLANNED_FROM[placeCity] === dayCitySlug;
}

export interface BestDay {
  readonly dayId: string;
  /** 0-based. */
  readonly index: number;
  /** False when no day of the trip is in the place's city. */
  readonly inCity: boolean;
}

export function bestDayFor(
  trip: Pick<Trip, "city" | "days">,
  placeCity: string | null | undefined,
): BestDay | null {
  if (trip.days.length === 0) return null;
  const days = trip.days.map((day, index) => ({ day, index }));
  const inCity = days.filter(({ day }) => placeFitsCity(placeCity, dayCity(trip, day)));
  const pool = inCity.length > 0 ? inCity : days;
  const roomy = pool.find(({ day }) => day.stops.length < DAY_ROOM);
  const pick =
    roomy ?? pool.reduce((best, d) => (d.day.stops.length < best.day.stops.length ? d : best), pool[0]!);
  return { dayId: pick.day.id, index: pick.index, inCity: inCity.length > 0 };
}

/** Where a place already sits in a trip, if it does: its first stop. */
export function stopOfPlace(
  trip: Pick<Trip, "days">,
  itemId: string,
): { dayId: string; index: number; stopId: string } | null {
  for (let index = 0; index < trip.days.length; index += 1) {
    const day = trip.days[index]!;
    const stop = day.stops.find((s) => s.itemId === itemId);
    if (stop) return { dayId: day.id, index, stopId: stop.id };
  }
  return null;
}
